import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import * as CertificateBatchConstants from "../../../constants/CertificateBatchConstants";
import { getCertificateBatch } from "../../../services/CertificateBatchService";
import { isPending } from "../../../helpers/CertificateBatchHelper";
import { formatDateTime } from "../../../helpers/ScheduleSummary";
import downloadBatch, { downloadFailureMessageId } from "./downloadBatch";

// The link in the "certificates ready" email. The file is fetched only when
// the user clicks Download: the API records every download.
const CertificateBatchDownload = () => {
  const { batchId } = useParams();
  const intl = useIntl();
  // { status: "loading" | "loaded" | "unavailable", batch, messageId }
  // messageId: why it is unavailable, when the API said (a failed download).
  const [page, setPage] = useState({ status: "loading" });
  const [downloading, setDownloading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    setPage({ status: "loading" });
    getCertificateBatch(batchId)
      .then((response) => {
        if (isCurrent) {
          const batch = response.data?.response;
          setPage(
            batch ? { status: "loaded", batch } : { status: "unavailable" }
          );
        }
      })
      .catch(() => {
        if (isCurrent) {
          setPage({ status: "unavailable" });
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [batchId, reloadKey]);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadBatch(batchId);
    } catch (error) {
      setPage({
        status: "unavailable",
        messageId: await downloadFailureMessageId(error),
      });
    } finally {
      setDownloading(false);
    }
  };

  const batch = page.batch;

  const renderBatch = () => {
    if (batch.canDownload) {
      return (
        <>
          <p className="m-0">
            {intl.formatMessage({ id: "certificate_batch_download_ready" })}
          </p>
          <div>
            {intl.formatMessage(
              { id: "certificate_batch_download_summary" },
              { item: batch.itemName, count: batch.certificateCount }
            )}
          </div>
          <small>
            {intl.formatMessage(
              { id: "certificate_batch_download_until" },
              { date: formatDateTime(batch.fileExpiresAtUtc, intl) }
            )}
          </small>
          <Button
            type="button"
            icon="pi pi-download"
            label={intl.formatMessage({ id: "certificate_batch_download" })}
            loading={downloading}
            onClick={download}
          />
        </>
      );
    }
    if (isPending(batch)) {
      return (
        <>
          <p className="m-0">
            {intl.formatMessage({ id: "certificate_batch_download_pending" })}
          </p>
          <Button
            type="button"
            icon="pi pi-refresh"
            label={intl.formatMessage({ id: "certificate_batch_refresh" })}
            outlined
            onClick={() => setReloadKey((key) => key + 1)}
          />
        </>
      );
    }
    if (
      batch.status === CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_FAILED
    ) {
      return (
        <p className="m-0">
          {intl.formatMessage(
            { id: "certificate_batch_download_failed_reason" },
            { reason: batch.failureReason || "-" }
          )}
        </p>
      );
    }
    return (
      <p className="m-0">
        {intl.formatMessage({ id: "certificate_batch_download_unavailable" })}
      </p>
    );
  };

  return (
    <div className="edexlms-page-content">
      <div className="edexlms-page-header">
        <div className="edexlms-page-title">
          {intl.formatMessage({ id: "certificate_batch_download_title" })}
        </div>
      </div>
      <div className="edexlms-page-body flex flex-column align-items-start gap-3">
        {(page.status === "loading" && (
          <div className="flex align-items-center gap-3">
            <ProgressSpinner style={{ width: "2rem", height: "2rem" }} />
            <span>
              {intl.formatMessage({ id: "certificate_batch_download_loading" })}
            </span>
          </div>
        )) ||
          null}
        {(page.status === "loaded" && renderBatch()) || null}
        {(page.status === "unavailable" && (
          <p className="m-0">
            {intl.formatMessage({
              id: page.messageId || "certificate_batch_download_unavailable",
            })}
          </p>
        )) ||
          null}
      </div>
    </div>
  );
};

export default CertificateBatchDownload;
